/** /tour_operators/{top_id}/access_groups/ — cross-tenant access group management.
 *
 * Lists are unpaginated (a tenant's group set is small); the grid builds a
 * client-side tree from `parent` ids. Template-managed groups reject every
 * mutation except membership and the manage toggle (HTTP 406, code
 * "managed_group").
 */
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './client';
import type { AccessTag, EmployeePermission, TemplateWarning } from './rbac';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

export interface AccessGroupRoleRef {
  id: number;
  name: string;
}

export interface AccessGroupTemplateRef {
  id: number;
  name: string;
  group_id: number;
}

export interface AccessGroupRow {
  id: number;
  tour_operator: number;
  name: string;
  codename: string | null;
  parent: number | null;
  managed: boolean;
  template: AccessGroupTemplateRef | null;
  member_count: number;
  full_access: boolean;
  is_guest_group: boolean;
  atag: { id: number; name: string }[];
  roles: AccessGroupRoleRef[];
  propagated_roles: AccessGroupRoleRef[];
  created_ts: string | null;
  updated_ts: string | null;
}

export interface AccessPermissionScope {
  id: number;
  agroup: number;
  perm: EmployeePermission;
  all_tags: boolean;
  atags: { id: number; name: string }[];
  target_agroups: { id: number; name: string; is_guest_group: boolean }[];
}

export interface AccessGroupDetail extends AccessGroupRow {
  permissions: AccessPermissionScope[];
  users: { id: number; email: string; display_name: string | null }[];
  bindings: { argument_id: number; argument_alias: string; atag: AccessTag }[];
  warnings?: TemplateWarning[];
  skipped?: unknown[];
}

export interface AccessGroupWriteInput {
  name?: string;
  codename?: string | null;
  parent?: number | null;
  atag_ids?: number[];
  role_ids?: number[];
  propagated_role_ids?: number[];
}

/** Cross-tenant list row: `tour_operator` is a {id,name,code} brief (the
 *  tenant-scoped list returns a bare id). */
export interface AccessGroupGlobalRow extends Omit<AccessGroupRow, 'tour_operator'> {
  /** `environment`: the Tenant cell marks a sandbox. */
  tour_operator: {
    id: number;
    name: string;
    code: string;
    environment?: TourOperatorEnvironment | null;
  };
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const base = (topId: number) => `/tour_operators/${topId}/access_groups/`;

export const accessGroupsApi = {
  list(topId: number, params?: { search?: string; managed?: boolean }) {
    return apiGet<{ results: AccessGroupRow[] }>(base(topId), {
      params: {
        search: params?.search || undefined,
        managed: params?.managed === undefined ? undefined : String(params.managed),
      },
    });
  },
  /** Cross-tenant paginated list — no tenant required. */
  globalList(params?: {
    limit?: number;
    offset?: number;
    ordering?: string;
    search?: string;
    managed?: boolean;
    /** One id or a comma list of ids (D1). */
    tour_operator?: TenantIdsParam;
    name_contains?: string;
    created_after?: string;
    created_before?: string;
  }) {
    return apiGet<PaginatedResponse<AccessGroupGlobalRow>>('/access_groups/', {
      params: {
        limit: params?.limit,
        offset: params?.offset,
        ordering: params?.ordering || undefined,
        search: params?.search || undefined,
        managed: params?.managed === undefined ? undefined : String(params.managed),
        tour_operator: params?.tour_operator,
        name_contains: params?.name_contains || undefined,
        created_after: params?.created_after || undefined,
        created_before: params?.created_before || undefined,
      },
    });
  },
  /** Retrieve one group by id, tenant resolved server-side (deep links). */
  retrieveById(id: number) {
    return apiGet<AccessGroupDetail>(`/access_groups/${id}/`);
  },
  retrieve(topId: number, id: number) {
    return apiGet<AccessGroupDetail>(`${base(topId)}${id}/`);
  },
  create(topId: number, body: AccessGroupWriteInput) {
    return apiPost<AccessGroupDetail>(base(topId), body);
  },
  update(topId: number, id: number, body: AccessGroupWriteInput) {
    return apiPatch<AccessGroupDetail>(`${base(topId)}${id}/`, body);
  },
  remove(topId: number, id: number) {
    return apiDelete<void>(`${base(topId)}${id}/`);
  },
  aps(topId: number, id: number, body: {
    aps_id: number;
    atags?: number[];
    target_agroups?: number[];
    all_tags?: boolean;
  }) {
    return apiPut<AccessGroupDetail>(`${base(topId)}${id}/aps/`, body);
  },
  addPermission(topId: number, id: number, scope: string, action: string) {
    return apiPost<AccessGroupDetail>(`${base(topId)}${id}/add_permission/`, {
      permission: { scope, action },
    });
  },
  batchPermissions(
    topId: number,
    id: number,
    actions: { op: 'add' | 'remove' | 'set_all_tags'; scope: string; action: string; all_tags?: boolean; value?: boolean }[],
  ) {
    return apiPost<AccessGroupDetail>(`${base(topId)}${id}/batch_permissions/`, { actions });
  },
  removePermission(topId: number, id: number, scope: string, action: string) {
    return apiPost<AccessGroupDetail>(`${base(topId)}${id}/remove_permission/`, {
      permission: { scope, action },
    });
  },
  removeUser(topId: number, id: number, userIds: number[]) {
    return apiPost<AccessGroupDetail>(`${base(topId)}${id}/remove_user/`, { users: userIds });
  },
  manage(topId: number, id: number, managed: boolean) {
    return apiPost<AccessGroupDetail>(`${base(topId)}${id}/manage/`, { managed });
  },
  refreshCache(topId: number, id: number) {
    return apiPost<void>(`${base(topId)}${id}/refresh_cache/`);
  },
};
